import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Delete, CalendarClock, Repeat, LogOut, ArrowLeft, Ban, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Logo } from "../components/Brand";
import { fmtDateStr } from "../lib/format";

const to12h = (t) => { if (!t) return ""; const p = t.split(":"); let h = Number(p[0]); const ap = h < 12 ? "AM" : "PM"; h = h % 12 || 12; return `${h}:${String(p[1]).padStart(2, "0")} ${ap}`; };
const STATUS_CLS = { approved: "bg-emerald-500/10 text-emerald-400", denied: "bg-rose-500/10 text-rose-400", pending: "bg-amber-500/10 text-amber-400" };

export default function EmployeePortal() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [swapShiftId, setSwapShiftId] = useState(null);
  const [swapTarget, setSwapTarget] = useState("");
  const [availForm, setAvailForm] = useState({ scope: "weekday", weekday: 6, date: "", all_day: true, start_time: "09:00", end_time: "17:00", note: "" });

  useEffect(() => {
    if (!companyId) return;
    api.get(`/public/companies/${companyId}`).then(({ data }) => setCompany(data)).catch(() => setCompany(false));
  }, [companyId]);

  const accent = company?.primary_color || "#10b981";

  const submit = useCallback(async (code) => {
    setLoading(true);
    try {
      const { data } = await api.post("/employee/portal", { company_id: companyId, pin: code });
      setData(data);
    } catch (err) { toastError(err); setPin(""); }
    finally { setLoading(false); }
  }, [companyId]);

  const toastError = (err) => {
    import("sonner").then(({ toast }) => toast.error(apiError(err.response?.data?.detail) || "Invalid PIN"));
  };

  const onKey = (val) => {
    if (val === "del") { setPin((p) => p.slice(0, -1)); return; }
    if (val === "ok") { if (pin.length >= 4) submit(pin); return; }
    if (loading) return;
    setPin((p) => (p + val).slice(0, 8));
  };

  const reloadPortal = useCallback(async () => {
    try { const { data } = await api.post("/employee/portal", { company_id: companyId, pin }); setData(data); } catch { /* noop */ }
  }, [companyId, pin]);
  const requestSwap = async () => {
    if (!swapShiftId || !swapTarget) { toast.error("Pick a shift and a coworker"); return; }
    try {
      await api.post("/employee/swap-request", { company_id: companyId, pin, schedule_id: swapShiftId, target_employee_id: swapTarget });
      toast.success("Swap request sent to your manager"); setSwapShiftId(null); setSwapTarget(""); reloadPortal();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not send"); }
  };
  const addAvail = async () => {
    if (availForm.scope === "date" && !availForm.date) { toast.error("Pick a date"); return; }
    try {
      await api.post("/employee/availability", {
        company_id: companyId, pin, scope: availForm.scope, all_day: availForm.all_day,
        date: availForm.scope === "date" ? availForm.date : null,
        weekday: availForm.scope === "weekday" ? Number(availForm.weekday) : null,
        start_time: availForm.all_day ? null : availForm.start_time,
        end_time: availForm.all_day ? null : availForm.end_time, note: availForm.note || null,
      });
      toast.success("Saved — your manager is warned if a shift lands here"); reloadPortal();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save"); }
  };
  const delAvail = async (id) => {
    try { await api.delete(`/employee/availability/${id}?company_id=${companyId}&pin=${encodeURIComponent(pin)}`); reloadPortal(); }
    catch { toast.error("Could not remove"); }
  };
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const availLabel = (a) => {
    const when = a.scope === "weekday" ? `Every ${WEEKDAYS[a.weekday] || "?"}` : fmtDateStr(a.date);
    const time = a.all_day ? "all day" : `${to12h(a.start_time)}–${to12h(a.end_time)}`;
    return `${when} · ${time}`;
  };

  if (company === false) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400" data-testid="portal-invalid">Company not found.</div>;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center px-4 py-8" style={{ background: `radial-gradient(1200px 500px at 50% -10%, ${accent}22, #020617 55%)` }}>
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          {company?.logo_path ? <Logo company={company} className="h-12 mb-2" /> : <div className="h-12" />}
          <h1 className="text-xl font-bold">{company?.name || "Employee portal"}</h1>
          <p className="text-slate-400 text-sm">Your schedule & swap history</p>
        </div>

        {!data ? (
          <motion.div key="pin" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900/70 border border-slate-800 rounded-3xl p-6">
            <p className="text-center text-slate-300 mb-4 text-sm">Enter your PIN to continue</p>
            <div className="flex justify-center gap-2 mb-5" data-testid="portal-pin-display">
              {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
                <span key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? "" : "opacity-25"}`} style={{ backgroundColor: i < pin.length ? accent : "#475569" }} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((n) => (
                <button key={n} data-testid={`portal-key-${n}`} onClick={() => onKey(n)} className="h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 text-2xl font-semibold transition-colors">{n}</button>
              ))}
              <button data-testid="portal-key-del" onClick={() => onKey("del")} className="h-14 rounded-2xl bg-slate-800/60 hover:bg-slate-700 flex items-center justify-center transition-colors"><Delete className="w-5 h-5" /></button>
              <button data-testid="portal-key-0" onClick={() => onKey("0")} className="h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 text-2xl font-semibold transition-colors">0</button>
              <button data-testid="portal-key-ok" onClick={() => onKey("ok")} disabled={pin.length < 4 || loading} className="h-14 rounded-2xl text-white font-semibold disabled:opacity-40 transition-colors" style={{ backgroundColor: accent }}>{loading ? "…" : "Go"}</button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="data" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold" data-testid="portal-employee-name">Hi, {data.employee.name}</span>
              <button data-testid="portal-signout" onClick={() => { setData(null); setPin(""); }} className="text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"><LogOut className="w-4 h-4" /> Exit</button>
            </div>

            <div className="bg-slate-900/70 border border-slate-800 rounded-2xl overflow-hidden" data-testid="portal-shifts">
              <div className="px-4 py-3 border-b border-slate-800 text-sm text-slate-300 font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4" style={{ color: accent }} /> Upcoming shifts</div>
              <div className="divide-y divide-slate-800/60">
                {data.shifts.map((s) => (
                  <div key={s.id} className="px-4 py-3" data-testid={`portal-shift-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm text-slate-200">{fmtDateStr(s.date)}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-slate-300">{to12h(s.start_time)} – {to12h(s.end_time)}</span>
                        <button data-testid={`portal-swap-btn-${s.id}`} onClick={() => { setSwapShiftId(swapShiftId === s.id ? null : s.id); setSwapTarget(""); }} className="text-xs inline-flex items-center gap-1 text-slate-400 hover:text-slate-100 transition-colors"><Repeat className="w-3.5 h-3.5" /> Swap</button>
                      </div>
                    </div>
                    {swapShiftId === s.id && (
                      <div className="mt-3 flex items-center gap-2" data-testid={`portal-swap-form-${s.id}`}>
                        <select data-testid="portal-swap-target" value={swapTarget} onChange={(e) => setSwapTarget(e.target.value)} className="flex-1 rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm focus:border-blue-500 outline-none">
                          <option value="">Swap with…</option>
                          {(data.coworkers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button data-testid="portal-swap-send" onClick={requestSwap} className="rounded-xl px-3 py-2 text-sm font-semibold text-white transition-transform active:scale-95" style={{ backgroundColor: accent }}>Send</button>
                      </div>
                    )}
                  </div>
                ))}
                {data.shifts.length === 0 && <div className="px-4 py-8 text-center text-slate-500 text-sm">No published shifts yet.</div>}
              </div>
            </div>

            <div className="bg-slate-900/70 border border-slate-800 rounded-2xl overflow-hidden" data-testid="portal-swaps">
              <div className="px-4 py-3 border-b border-slate-800 text-sm text-slate-300 font-semibold flex items-center gap-2"><Repeat className="w-4 h-4" style={{ color: accent }} /> Swap history</div>
              <div className="divide-y divide-slate-800/60">
                {data.swaps.map((s) => (
                  <div key={s.id} className="px-4 py-3 flex items-center justify-between gap-2" data-testid={`portal-swap-${s.id}`}>
                    <div className="text-sm">
                      <span className="text-slate-400">{s.direction === "out" ? "You → " : "From "}</span>
                      <span className="text-slate-200">{s.direction === "out" ? s.target_name : s.requester_name}</span>
                      <div className="text-[11px] text-slate-500 font-mono">{s.schedule?.date ? `${fmtDateStr(s.schedule.date)} · ${to12h(s.schedule.start_time)}–${to12h(s.schedule.end_time)}` : "shift removed"}</div>
                    </div>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${STATUS_CLS[s.status] || STATUS_CLS.pending}`}>{s.status}</span>
                  </div>
                ))}
                {data.swaps.length === 0 && <div className="px-4 py-8 text-center text-slate-500 text-sm">No swap requests yet.</div>}
              </div>
            </div>

            <div className="bg-slate-900/70 border border-slate-800 rounded-2xl overflow-hidden" data-testid="portal-availability">
              <div className="px-4 py-3 border-b border-slate-800 text-sm text-slate-300 font-semibold flex items-center gap-2"><Ban className="w-4 h-4" style={{ color: accent }} /> When I can't work</div>
              <div className="divide-y divide-slate-800/60">
                {(data.availability || []).map((a) => (
                  <div key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-2" data-testid={`portal-avail-${a.id}`}>
                    <span className="text-sm text-slate-300">{availLabel(a)}{a.note ? ` · ${a.note}` : ""}</span>
                    <button data-testid={`portal-avail-del-${a.id}`} onClick={() => delAvail(a.id)} className="p-1.5 text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {(data.availability || []).length === 0 && <div className="px-4 py-3 text-center text-slate-500 text-sm">Add times you're unavailable so you don't get scheduled then.</div>}
              </div>
              <div className="p-4 border-t border-slate-800 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="portal-avail-scope" value={availForm.scope} onChange={(e) => setAvailForm((f) => ({ ...f, scope: e.target.value }))} className="rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm outline-none focus:border-blue-500">
                    <option value="weekday">Every week</option>
                    <option value="date">A specific date</option>
                  </select>
                  {availForm.scope === "weekday" ? (
                    <select data-testid="portal-avail-weekday" value={availForm.weekday} onChange={(e) => setAvailForm((f) => ({ ...f, weekday: e.target.value }))} className="rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm outline-none focus:border-blue-500">
                      {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  ) : (
                    <input data-testid="portal-avail-date" type="date" value={availForm.date} onChange={(e) => setAvailForm((f) => ({ ...f, date: e.target.value }))} className="rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="portal-avail-allday" type="checkbox" checked={availForm.all_day} onChange={(e) => setAvailForm((f) => ({ ...f, all_day: e.target.checked }))} /> All day</label>
                {!availForm.all_day && (
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="portal-avail-start" type="time" value={availForm.start_time} onChange={(e) => setAvailForm((f) => ({ ...f, start_time: e.target.value }))} className="rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                    <input data-testid="portal-avail-end" type="time" value={availForm.end_time} onChange={(e) => setAvailForm((f) => ({ ...f, end_time: e.target.value }))} className="rounded-xl bg-slate-950 border border-slate-700 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                  </div>
                )}
                <button data-testid="portal-avail-add" onClick={addAvail} className="w-full rounded-xl py-2.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 transition-transform active:scale-95" style={{ backgroundColor: accent }}><Plus className="w-4 h-4" /> Add unavailable time</button>
              </div>
            </div>

            <a href={`/mobile/${companyId}`} data-testid="portal-clock-link" className="flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors pt-1">
              <ArrowLeft className="w-4 h-4" /> Go to clock in / out
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
}
